import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import HelpTooltip from '@/components/help/HelpTooltip';
import { useAuth } from '@/contexts/AuthContext';
import { Zap, Plus, Play, Pause, HelpCircle, Clock, Send, Bell, Star, MessageCircle, StopCircle, UserCheck, PhoneCall, PhoneForwarded, Smartphone, Mail, Server } from 'lucide-react';
import AutomationsMap from '@/components/AutomationsMap';

const TRIGGER_LABELS = {
  lead_converted: 'Cuando llega un cliente nuevo',
  conversation_closed: 'Cuando se cierra una conversación',
  appointment_scheduled: 'Cuando agendan una cita',
  lead_birthday: 'El día de su cumpleaños',
  lead_reactivation: 'Cuando un cliente lleva tiempo sin contacto',
  lead_expiry_approaching: 'Cuando se acerca un vencimiento',
  appointment_reminder: 'Antes de su cita',
  manual: 'Cuando tú la lances',
};
const STEP_TYPES = [
  { value: 'send_template', label: 'Enviar plantilla WhatsApp', icon: Send },
  { value: 'wait', label: 'Esperar tiempo', icon: Clock },
  { value: 'notify_seller', label: 'Notificar al vendedor', icon: Bell },
  { value: 'ask_nps', label: 'Preguntar NPS (1-5)', icon: Star, help: 'Mide si recomendaría tu negocio. Escala del 1 al 5.' },
  { value: 'ask_csat', label: 'Preguntar CSAT (1-5)', icon: Star, help: 'Mide qué tan satisfecho quedó con el servicio. Escala del 1 al 5.' },
  { value: 'ask_ces', label: 'Preguntar CES (1-5)', icon: Star, help: 'Mide qué tan fácil fue resolver su trámite. Menos esfuerzo = mejor calificación.' },
  { value: 'stop_if_replied', label: 'Detener si el lead respondió', icon: StopCircle },
  { value: 'send_whatsapp_direct', label: 'Enviar mensaje de WhatsApp (seguimiento)', icon: MessageCircle },
  { value: 'send_sms', label: 'Enviar SMS', icon: Smartphone, help: 'Mensaje de texto por Twilio (misma cuenta que llamadas). Requiere consentimiento SMS.' },
  { value: 'outbound_voice_call', label: 'Llamar por teléfono con IA', icon: PhoneCall },
  { value: 'connect_lead_to_vendor', label: 'Conectar lead con vendedor (Twilio)', icon: PhoneForwarded, help: 'Marca al lead y lo enlaza con su vendedor por voz. IVR: el lead presiona 1. Bridge: marca a ambos a la vez. Si falla la voz, puede avisar por WhatsApp.' },
  { value: 'send_email', label: 'Enviar email', icon: Mail, help: 'Manda un correo con una plantilla de Marketing → Email (variables {{nombre_cliente}}, logo del tenant). Solo llega si el contacto tiene correo y no pidió dejar de recibir mensajes.' },
  { value: 'mcp_tool_call', label: 'Ejecutar herramienta MCP', icon: Server, help: 'Corre una acción externa que conectaste en Integraciones → MCP (por ejemplo consultar un sistema o actualizar un dato). No envía mensajes al cliente por sí sola.' },
  { value: 'escalate_to_human', label: 'Escalar al vendedor asignado', icon: UserCheck },
];

// Sprint 32 — sequence a non-technical owner can use as-is: wait, check if the
// lead already replied (stop if so), nudge once by WhatsApp, wait longer, check
// again, and hand off to the human seller if still nothing.

// S84 — Recetas listas (1 clic). El dueño elige plantilla de email al activar.
const FEATURED_RECIPE_IDS = ['welcome', 'pipeline_nurture', 'followup_soft', 'bday_hello', 'appt_remind', 'reactivate', 'appt_confirm', 'welcome_wa', 'promo_month'];

const EMAIL_RECIPES = [
  { id: 'bday_hello', stock_key: 'stock_cumple', name: 'Cumpleaños — felicitación', channel: 'email', trigger_type: 'lead_birthday',
    blurb: 'Cada año, el día del cumpleaños, manda un correo cálido con tu marca.',
    steps: [{ type: 'send_email', config: { email_template_id: '' } }] },
  { id: 'bday_coupon', stock_key: 'stock_cumple', name: 'Cumpleaños — cupón / oferta', channel: 'email', trigger_type: 'lead_birthday',
    blurb: 'Felicitación con un detalle o cupón de regalo.',
    steps: [{ type: 'send_email', config: { email_template_id: '' } }] },
  { id: 'appt_confirm', stock_key: 'stock_cita', name: 'Cita creada — confirmación', channel: 'email', trigger_type: 'appointment_scheduled',
    blurb: 'En cuanto agendan, confirma fecha y datos por correo.',
    steps: [{ type: 'send_email', config: { email_template_id: '' } }] },
  { id: 'appt_remind', stock_key: 'stock_cita', name: 'Recordatorio de cita', channel: 'email', trigger_type: 'appointment_reminder',
    blurb: 'Le recuerda su cita con tiempo para que no falte.',
    steps: [{ type: 'send_email', config: { email_template_id: '' } }] },
  { id: 'welcome', stock_key: 'stock_bienvenida', name: 'Bienvenida al captar lead', channel: 'email', trigger_type: 'lead_converted',
    blurb: 'Primer contacto de marca cuando entra un lead nuevo.',
    steps: [{ type: 'send_email', config: { email_template_id: '' } }] },
  { id: 'welcome_wa', stock_key: 'stock_bienvenida', name: 'Bienvenida WhatsApp + email', channel: 'mix', trigger_type: 'lead_converted',
    blurb: 'WA inmediato y email de bienvenida en el mismo flujo.',
    steps: [
      { type: 'send_whatsapp_direct', config: { text: '¡Hola {{nombre}}! Gracias por contactarnos. En breve te escribimos 🙂' } },
      { type: 'send_email', config: { email_template_id: '' } },
    ] },
  { id: 'reactivate', stock_key: 'stock_reactivacion', name: 'Reactivar inactivos', channel: 'email', trigger_type: 'lead_reactivation',
    blurb: 'A clientes fríos les manda un «te extrañamos» con tu marca.',
    steps: [{ type: 'send_email', config: { email_template_id: '' } }] },
  { id: 'expiry', stock_key: 'stock_promo', name: 'Aviso de vencimiento', channel: 'email', trigger_type: 'lead_expiry_approaching',
    blurb: 'Cuando se acerca una fecha de vigencia en el lead.',
    steps: [{ type: 'send_email', config: { email_template_id: '' } }] },
  { id: 'post_close_nps', name: 'Post conversación — NPS', channel: 'mix', trigger_type: 'conversation_closed',
    blurb: 'Al cerrar el chat, pide NPS y notifica al vendedor.',
    steps: [
      { type: 'wait', config: { hours: 1 } },
      { type: 'ask_nps', config: {} },
      { type: 'notify_seller', config: {} },
    ] },
  { id: 'followup_soft', name: 'Seguimiento suave 2h+24h', channel: 'wa', trigger_type: 'lead_converted',
    blurb: 'Plantilla clásica de seguimiento sin saturar.',
    steps: [
      { type: 'wait', config: { hours: 2 } },
      { type: 'stop_if_replied', config: {} },
      { type: 'send_whatsapp_direct', config: { text: 'Hola, ¿sigues buscando información? Aquí estamos para ayudarte.' } },
      { type: 'wait', config: { hours: 24 } },
      { type: 'stop_if_replied', config: {} },
      { type: 'escalate_to_human', config: {} },
    ] },
  { id: 'promo_month', stock_key: 'stock_promo', name: 'Promo del mes (manual)', channel: 'email', trigger_type: 'manual',
    blurb: 'La enciendes solo cuando quieras lanzar una promo.',
    steps: [{ type: 'send_email', config: { email_template_id: '' } }] },
  { id: 'review_ask', stock_key: 'stock_bienvenida', name: 'Pedir reseña post-cita', channel: 'email', trigger_type: 'appointment_scheduled',
    blurb: 'Al día siguiente de la cita, pide una reseña con cariño.',
    steps: [
      { type: 'wait', config: { days: 1 } },
      { type: 'send_email', config: { email_template_id: '' } },
    ] },
  { id: 'no_reply_email', stock_key: 'stock_bienvenida', name: 'No contestó WA → email', channel: 'mix', trigger_type: 'lead_converted',
    blurb: 'Si no responde WhatsApp, intenta por correo.',
    steps: [
      { type: 'wait', config: { hours: 4 } },
      { type: 'stop_if_replied', config: {} },
      { type: 'send_whatsapp_direct', config: { text: '¿Pudiste ver nuestra info? Cualquier duda, aquí estamos.' } },
      { type: 'wait', config: { hours: 20 } },
      { type: 'stop_if_replied', config: {} },
      { type: 'send_email', config: { email_template_id: '' } },
    ] },
  { id: 'csat_service', name: 'CSAT después de servicio', channel: 'mix', trigger_type: 'conversation_closed',
    blurb: 'Mide satisfacción del servicio al cerrar conversación.',
    steps: [{ type: 'wait', config: { hours: 2 } }, { type: 'ask_csat', config: {} }] },
  { id: 'vip_voice', name: 'Lead caliente → llamada IA', channel: 'voice', trigger_type: 'lead_converted',
    blurb: 'Para leads de alto valor: la IA marca por teléfono.',
    steps: [{ type: 'wait', config: { minutes: 15 } }, { type: 'outbound_voice_call', config: {} }] },
  { id: 'sms_remind', name: 'Recordatorio SMS de cita', channel: 'sms', trigger_type: 'appointment_reminder',
    blurb: 'Complemento al email: SMS corto con la hora.',
    steps: [{ type: 'send_sms', config: { text: 'Recordatorio: tienes cita con nosotros. ¡Te esperamos!' } }] },
  { id: 'pipeline_nurture', stock_key: 'stock_promo', name: 'Nutrición 3 toques', channel: 'email', trigger_type: 'lead_converted',
    blurb: 'Serie corta: bienvenida → valor → CTA (3 emails con esperas).',
    steps: [
      { type: 'send_email', config: { email_template_id: '' } },
      { type: 'wait', config: { days: 2 } },
      { type: 'send_email', config: { email_template_id: '' } },
      { type: 'wait', config: { days: 3 } },
      { type: 'send_email', config: { email_template_id: '' } },
    ] },
  { id: 'cart_react', stock_key: 'stock_reactivacion', name: 'Volver a visitar (win-back)', channel: 'email', trigger_type: 'lead_reactivation',
    blurb: 'Misma lógica de inactivos con tono comercial.',
    steps: [{ type: 'send_email', config: { email_template_id: '' } }] },
  { id: 'human_handoff', name: 'Escalar a humano si no responde', channel: 'wa', trigger_type: 'lead_converted',
    blurb: 'Después de un toque, pasa al vendedor asignado.',
    steps: [
      { type: 'wait', config: { hours: 6 } },
      { type: 'stop_if_replied', config: {} },
      { type: 'escalate_to_human', config: { message: 'Lead sin respuesta — contactar hoy' } },
    ] },
  { id: 'new_year', stock_key: 'stock_ano_nuevo', name: 'Saludo de temporada (manual)', channel: 'email', trigger_type: 'manual',
    blurb: 'Para fechas especiales: Año Nuevo, Buen Fin, etc.',
    steps: [{ type: 'send_email', config: { email_template_id: '' } }] },
  { id: 'multi_confirm', stock_key: 'stock_cita', name: 'Cita: email + WhatsApp', channel: 'mix', trigger_type: 'appointment_scheduled',
    blurb: 'Confirmación por dos canales al agendar.',
    steps: [
      { type: 'send_email', config: { email_template_id: '' } },
      { type: 'send_whatsapp_direct', config: { text: '¡Cita confirmada! Te esperamos. Si necesitas reprogramar, avísanos.' } },
    ] },
];


const FOLLOWUP_TEMPLATE_STEPS = [
  { type: 'wait', config: { hours: 2 } },
  { type: 'stop_if_replied', config: {} },
  { type: 'send_whatsapp_direct', config: { text: 'Hola, vimos que nos escribiste. ¿Sigues buscando información? Aquí estamos para ayudarte 🙂' } },
  { type: 'wait', config: { hours: 24 } },
  { type: 'stop_if_replied', config: {} },
  { type: 'escalate_to_human', config: {} },
];
const STATUS_COLORS = { draft: 'bg-gray-100 text-gray-600', active: 'bg-emerald-100 text-emerald-700', paused: 'bg-amber-100 text-amber-700' };
const STATUS_LABELS = { draft: 'Borrador', active: 'Activa', paused: 'Pausada' };
const RUN_STATUS_LABELS = { running: 'En curso', waiting: 'Esperando', completed: 'Completada', failed: 'Con error' };
// Encuestas que un paso "Preguntar NPS/CSAT/CES" deja marcadas como pendientes.
// El cliente contesta por WhatsApp o teléfono y alguien del equipo captura aquí la calificación.
const SURVEY_LABELS = {
  nps: 'NPS — ¿nos recomendaría?',
  csat: 'CSAT — ¿quedó satisfecho?',
  ces: 'CES — ¿qué tan fácil fue su trámite?',
};
const SURVEY_TYPES = ['nps', 'csat', 'ces'];
const SURVEY_SCORE_HINTS = { 1: '1 = muy mal', 5: '5 = excelente' };
const WAIT_UNITS = [
  { value: 'minutes', label: 'minutos' },
  { value: 'hours', label: 'horas' },
  { value: 'days', label: 'días' },
];

const getWaitDisplay = (config = {}) => {
  if (config.minutes) return { unit: 'minutes', value: config.minutes };
  if (config.days) return { unit: 'days', value: config.days };
  return { unit: 'hours', value: config.hours ?? 24 };
};

const buildWaitConfig = (unit, value) => {
  const v = Math.max(1, parseInt(value, 10) || 1);
  if (unit === 'minutes') return { minutes: v, hours: 0, days: 0 };
  if (unit === 'days') return { minutes: 0, hours: 0, days: v };
  return { minutes: 0, hours: v, days: 0 };
};

export default function MarketingAutomations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isEmailHub = location.pathname.includes('/email');
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightRecipe = searchParams.get('recipe') || '';

  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showRuns, setShowRuns] = useState(null);
  const [runTestFlow, setRunTestFlow] = useState(null);
  // Solo dueño y administrador pueden lanzar una prueba (el servidor exige lo mismo).
  const canRunTest = user?.tenant_role === 'tenant_owner' || user?.tenant_role === 'tenant_admin';
  const [recipeFilter, setRecipeFilter] = useState(() =>
    (typeof window !== 'undefined' && window.location.pathname.includes('/email')) ? 'email' : 'featured'
  );
  const [activity, setActivity] = useState(null);
  const [activatingId, setActivatingId] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const loadActivity = () => {
    api.get('/tenant/marketing/automations/activity')
      .then(r => setActivity(r.data))
      .catch(() => setActivity(null));
  };

  const applyRecipe = (recipe) => {
    setEditing({
      name: recipe.name,
      trigger_type: recipe.trigger_type,
      steps: recipe.steps.map(s => ({ type: s.type, config: { ...(s.config || {}) } })),
      stock_key: recipe.stock_key || null,
      _recipe: recipe.id,
    });
    setShowEditor(true);
  };

  /** S86 — 1 clic: clona plantilla + crea + activa */
  const activateRecipeNow = async (recipe) => {
    setActivatingId(recipe.id);
    try {
      const needsEmail = (recipe.steps || []).some(s => s.type === 'send_email');
      const { data } = await api.post('/tenant/marketing/automations/from-recipe', {
        name: recipe.name,
        trigger_type: recipe.trigger_type,
        steps: recipe.steps.map(s => ({ type: s.type, config: { ...(s.config || {}) } })),
        stock_key: needsEmail ? (recipe.stock_key || null) : null,
        recipe_id: recipe.id,
        activate: true,
      });
      if (data.activated || data.already_active) {
        toast.success(data.message || 'Listo. Ya está activa.');
      } else {
        toast.message(data.message || `«${recipe.name}» quedó en borrador`);
      }
      load();
      loadActivity();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = formatApiError(d) || 'No se pudo activar';
      // Plan / límite en lenguaje de negocio
      if (String(msg).toLowerCase().includes('plan') || String(msg).toLowerCase().includes('límite') || String(msg).toLowerCase().includes('limite')) {
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setActivatingId('');
    }
  };

  const isRecipeActive = (recipeId) => {
    const recipe = EMAIL_RECIPES.find(r => r.id === recipeId);
    return flows.some(f => {
      if (f.status !== 'active') return false;
      if (f.trigger_config?.recipe_id === recipeId) return true;
      // flujos viejos sin recipe_id: mismo nombre + mismo disparo
      if (recipe && f.name === recipe.name && f.trigger_type === recipe.trigger_type) return true;
      return false;
    });
  };


  const load = () => {
    setLoading(true);
    api.get('/tenant/marketing/automations').then(r => setFlows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    loadActivity();
  };
  useEffect(load, []);

  useEffect(() => {
    if (!highlightRecipe) return;
    const el = document.querySelector(`[data-testid="recipe-${highlightRecipe}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightRecipe, loading]);


  const toggle = async (f) => {
    try {
      const action = f.status === 'active' ? 'pause' : 'activate';
      await api.post(`/tenant/marketing/automations/${f.id}/${action}`);
      toast.success(action === 'activate' ? 'Secuencia activada' : 'Secuencia pausada');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="marketing-automations">
      <AutomationsMap current="sequences" />

      <div data-testid="automations-nurture-header" className="rounded-xl border border-[#F72585]/20 bg-[#FFF5F9] p-4">
        <p className="text-sm font-bold text-[#0F172A]">Estás en: Secuencias</p>
        <p className="text-xs text-[#64748B] mt-1">
          NO son envíos masivos. Son cadenas que se disparan solas con el tiempo o una fecha:
          bienvenida, cita, cumpleaños, reactivación. Elige una receta y pulsa «Activar ya».
        </p>
        <p className="text-[11px] text-[#64748B] mt-2">
          Tip: desde la <strong>Ruta del lead</strong> puedes enlazar una secuencia con el nodo «Secuencia automática».
        </p>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-[#0F172A]">{isEmailHub ? 'Secuencias de correo' : 'Tus secuencias'}</h2>
          <Button variant="ghost" size="sm" onClick={() => setShowHelp(true)}><HelpCircle className="w-4 h-4 text-[#94A3B8]" /></Button>
        </div>
        <button type="button" onClick={() => setShowAdvanced(v => !v)}
          className="text-[11px] text-[#94A3B8] hover:text-[#0F172A] underline" data-testid="auto-advanced-toggle">
          {showAdvanced ? 'Ocultar opciones avanzadas' : 'Opciones avanzadas'}
        </button>
      </div>
      <p className="text-sm text-[#64748B]">Elige una, pulsa <strong>Activar ya</strong> y listo. El sistema se encarga del resto.</p>
      {showAdvanced && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => { setEditing({ name: 'Seguimiento de leads', trigger_type: 'lead_converted', steps: FOLLOWUP_TEMPLATE_STEPS }); setShowEditor(true); }} data-testid="automation-template-btn">
            <Zap className="w-4 h-4 mr-1" /> Seguimiento avanzado
          </Button>
          <Button size="sm" onClick={() => { setEditing(null); setShowEditor(true); }} className="bg-[#0F172A] text-white" data-testid="automation-create-btn">
            <Plus className="w-4 h-4 mr-1" /> Crear a mano
          </Button>
        </div>
      )}

      {/* Resumen simple — no sermón de datos incompletos */}
      {activity && (
        <div className="grid grid-cols-2 gap-2" data-testid="automation-activity">
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-3">
            <p className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">Funciones activas</p>
            <p className="text-2xl font-bold text-emerald-600">{activity.flows?.active ?? 0}</p>
            <p className="text-[10px] text-[#64748B]">encendidas en tu negocio</p>
          </div>
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-3">
            <p className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">Hoy se enviaron</p>
            <p className="text-2xl font-bold text-[#0F172A]">{activity.today?.sent ?? 0}</p>
            <p className="text-[10px] text-[#64748B]">correos automáticos</p>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 px-4 py-3 text-sm text-[#0F172A]" data-testid="auto-simple-hint">
        <strong>Un clic y listo.</strong> Si está en tu plan, actívala y olvídate. El sistema manda cuando toque.
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-3" data-testid="automation-recipes">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-[#0F172A]">Activa y olvídate</h3>
            <p className="text-[11px] text-[#64748B]">
              Cumpleaños, citas, bienvenida… si está en tu plan, enciéndela y el sistema se encarga.
            </p>
          </div>
          <div className="flex flex-wrap gap-1">
            {(isEmailHub
              ? [['email','Email'],['featured','Populares'],['all','Todas'],['mix','Email + WA']]
              : [['featured','Populares'],['all','Todas'],['email','Email'],['mix','Mixto'],['wa','WhatsApp']]
            ).map(([k,l]) => (
              <button key={k} type="button" onClick={() => setRecipeFilter(k)}
                className={`text-[10px] px-2 py-1 rounded-full border ${recipeFilter===k ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[420px] overflow-y-auto pr-1">
          {EMAIL_RECIPES
            .filter(r => {
              if (recipeFilter === 'featured') return FEATURED_RECIPE_IDS.includes(r.id);
              if (recipeFilter === 'all') return !isEmailHub || r.channel === 'email' || r.channel === 'mix';
              if (recipeFilter === 'email') return r.channel === 'email';
              if (recipeFilter === 'mix') return r.channel === 'mix';
              return r.channel === recipeFilter;
            })
            .map(r => {
              const stockLabel = (r.stock_key || '').replace('stock_', '').replace(/_/g, ' ') || 'personalizada';
              const hi = highlightRecipe === r.id;
              return (
            <div key={r.id}
              className={`text-left border rounded-lg p-3 transition-colors flex flex-col ${
                hi ? 'border-[#F72585] ring-2 ring-[#F72585]/30 bg-[#FFF5F9]' : 'border-[#E2E8F0] hover:border-[#F72585]/40 hover:bg-[#FFF5F9]'
              }`}
              data-testid={`recipe-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-semibold text-[#0F172A]">{r.name}</p>
                {isRecipeActive(r.id) && (
                  <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    Activa
                  </span>
                )}
              </div>
              <p className="text-[11px] text-[#64748B] mt-1 line-clamp-2 flex-1">{r.blurb}</p>
              <div className="mt-3 flex flex-col gap-1.5">
                <button type="button" disabled={activatingId === r.id || isRecipeActive(r.id)}
                  onClick={() => activateRecipeNow(r)}
                  className={`w-full text-xs font-semibold rounded-xl py-2 disabled:opacity-60 ${
                    isRecipeActive(r.id)
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-[#F72585] text-white hover:bg-[#D91A70]'
                  }`}
                  data-testid={`recipe-activate-${r.id}`}>
                  {activatingId === r.id ? 'Activando…' : isRecipeActive(r.id) ? '✓ Ya está activa' : 'Activar ya'}
                </button>
                <button type="button" onClick={() => applyRecipe(r)}
                  className="text-[10px] text-[#94A3B8] hover:text-[#0F172A] underline"
                  data-testid={`recipe-customize-${r.id}`}>
                  Personalizar (opcional)
                </button>
              </div>
            </div>
              );
            })}
        </div>
      </div>

      {flows.length === 0 && !loading ? (
        <div className="text-center py-12 bg-white border border-[#E2E8F0] rounded-xl">
          <Zap className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">Arriba elige una y pulsa «Activar ya». En segundos queda trabajando sola.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {flows.map(f => (
            <div key={f.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-[#0F172A]">{f.name}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[f.status]}`}>{STATUS_LABELS[f.status]}</span>
                  </div>
                  <p className="text-xs text-[#64748B] mt-0.5">{TRIGGER_LABELS[f.trigger_type] || f.trigger_type}</p>
                </div>
                <div className="flex gap-1 items-center flex-wrap justify-end">
                  <Button variant="outline" size="sm" onClick={() => toggle(f)} data-testid="automation-activate-btn">
                    {f.status === 'active' ? <><Pause className="w-3.5 h-3.5 mr-1" /> Pausar</> : <><Play className="w-3.5 h-3.5 mr-1" /> Activar</>}
                  </Button>
                  {canRunTest && (
                    <>
                      <Button variant="outline" size="sm" disabled={f.status !== 'active'} onClick={() => setRunTestFlow(f)} data-testid="automation-run-test-btn">
                        <Zap className="w-3.5 h-3.5 mr-1" /> Probar ahora
                      </Button>
                      <HelpTooltip text={f.status !== 'active'
                        ? 'Para poder probarla, primero actívala con el botón «Activar». Mientras esté en borrador o pausada no hace nada.'
                        : 'Lanza una prueba de esta automatización sin esperar a que ocurra el evento. No le manda mensajes a ningún cliente: sirve para revisar en el Historial que los pasos corran.'} />
                    </>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setShowRuns(f.id)}>Historial</Button>
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(f); setShowEditor(true); }}>Editar</Button>
                </div>
              </div>
              {f.steps?.length > 0 && (
                <div className="flex items-center gap-1.5 mt-2" data-testid="automation-step-list">
                  {f.steps.map((s, i) => {
                    const st = STEP_TYPES.find(t => t.value === s.type);
                    return <span key={i} className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-[#F1F5F9] text-[10px] text-[#64748B] rounded">
                      {st && <st.icon className="w-2.5 h-2.5" />} {st?.label || s.type}
                    </span>;
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {showEditor && <AutomationEditor initial={editing} onClose={() => setShowEditor(false)} onSaved={load} />}
      {showRuns && <RunsModal flowId={showRuns} onClose={() => setShowRuns(null)} />}
      {runTestFlow && (
        <RunTestDialog
          flow={runTestFlow}
          onClose={() => setRunTestFlow(null)}
          onDone={(flowId) => { load(); setShowRuns(flowId); }}
        />
      )}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent><DialogHeader><DialogTitle>¿Qué es una automatización?</DialogTitle></DialogHeader>
          <div className="text-sm text-[#64748B] space-y-2">
            <p>Una <strong>automatización</strong> ejecuta pasos automáticamente cuando ocurre un evento.</p>
            <p>Ejemplo: <em>Cuando llegue un lead nuevo → esperar 1 hora → enviar plantilla de bienvenida → esperar 3 días → preguntar NPS</em></p>
            <p>El <strong>NPS</strong> (Net Promoter Score) mide qué tan satisfecho quedó tu cliente del 1 al 5.</p>
            <p><strong>Seguimiento de leads que no responden</strong>: usa "Seguimiento avanzado" para crear en un clic una secuencia que espera, revisa si el lead ya contestó (y se detiene si sí), le manda un recordatorio, y si sigue sin responder, avisa al vendedor asignado.</p>
            <p><strong>Llamar por teléfono con IA</strong>: agrega este paso si además de WhatsApp quieres que tu asistente le marque por teléfono al lead y platique con él en vivo. No tienes que escribir nada — la IA arma el saludo sola usando el nombre de tu negocio. Eso sí, necesitas primero tener conectado un número de voz (en Integraciones, o pídele a tu administrador que te active la infraestructura compartida). Si el lead ya recibió una llamada en las últimas 4 horas, el sistema no vuelve a marcarle para no ser insistente.</p>
            <p><strong>CSAT y CES</strong>: igual que NPS, marcan la encuesta como pendiente para que el cliente responda después. CSAT = satisfacción; CES = facilidad del trámite.</p>
            <p><strong>SMS</strong>: envía un mensaje de texto usando tu cuenta Twilio (la misma de las llamadas). Solo llega si el contacto tiene consentimiento SMS. Fuera de horario laboral el envío se pospone automáticamente.</p>
            <p><strong>Cumpleaños / reactivación / vencimiento</strong>: elige esos disparadores si quieres que el flujo corra cuando el sistema detecte (una vez al día) un cumpleaños, un lead sin actividad reciente o una fecha de vencimiento próxima en la ficha del lead.</p>
            <p><strong>Enviar email</strong>: usa una plantilla de correo ya creada. Respeta el consentimiento de email del contacto y, fuera de horario laboral, pospone el envío en lugar de mandarlo de madrugada.</p>
            <p><strong>Ejecutar herramienta MCP</strong>: dispara una herramienta que sincronizaste desde un servidor MCP. Útil para conectar con otros sistemas sin escribir código. Requiere tener MCP activo en tu plan.</p>
            <p><strong>Conectar lead con vendedor (Twilio)</strong>: después de una espera, marca al lead y lo enlaza con su vendedor. En modo IVR el lead presiona 1 para hablar; en bridge se marcan a ambos a la vez. Requiere Twilio/voz configurado y un vendedor asignado al lead.</p>
            <p><strong>Probar ahora</strong>: lanza la automatización de inmediato, sin esperar a que ocurra el evento, para revisar que los pasos corran. Solo funciona si la automatización está <em>Activa</em>, y como no hay un cliente real de por medio, los pasos que envían mensajes quedan anotados como omitidos. Lo que pasó se ve en <em>Historial</em>. Solo el dueño o un administrador pueden usarlo.</p>
            <p><strong>Calificaciones de encuestas</strong>: cuando un flujo llega a un paso de NPS, CSAT o CES, esa ejecución queda marcada como <em>encuesta pendiente</em> en el <em>Historial</em>. Cuando el cliente te conteste (por WhatsApp, teléfono o en persona), abre el Historial y captura ahí su calificación del 1 al 5. Esas calificaciones son las que alimentan el tablero de satisfacción y los reportes.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AutomationEditor({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || '');
  const [trigger, setTrigger] = useState(initial?.trigger_type || 'lead_converted');
  const [steps, setSteps] = useState(initial?.steps || []);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [mcpTools, setMcpTools] = useState([]);

  useEffect(() => {
    api.get('/tenant/marketing/templates').then(r => setTemplates((r.data || []).filter(t => t.external_status === 'APPROVED' || t.status === 'draft'))).catch(() => {});
    api.get('/tenant/email/templates?status=active').then(r => setEmailTemplates(r.data || [])).catch(() => {});
    api.get('/tenant/mcp/servers').then(async r => {
      const servers = (r.data || []).filter(s => s.status !== 'archived');
      const all = [];
      for (const s of servers) {
        try {
          const tr = await api.get(`/tenant/mcp/servers/${s.id}/tools`);
          (tr.data || []).forEach(t => all.push({ ...t, server_name: s.name }));
        } catch { /* MCP no habilitado o sin permiso */ }
      }
      setMcpTools(all);
    }).catch(() => {});
  }, []);

  const addStep = (type) => {
    if (steps.length >= 5) { toast.error('Máximo 5 pasos'); return; }
    const config = type === 'wait' ? { hours: 24 }
      : type === 'send_template' ? { template_id: '' }
      : type === 'send_whatsapp_direct' ? { text: '' }
      : type === 'send_sms' ? { text: '' }
      : type === 'send_email' ? { email_template_id: '' }
      : type === 'mcp_tool_call' ? { mcp_server_tool_id: '', arguments: {} }
      : type === 'escalate_to_human' ? { message: '' }
      : type === 'connect_lead_to_vendor' ? { mode: 'ivr', vendor_resolution: 'assigned', cooldown_minutes: 5, fallback_notify_vendor: true, respect_business_hours: true }
      : {};
    setSteps([...steps, { type, config }]);
  };
  const removeStep = (i) => setSteps(steps.filter((_, idx) => idx !== i));
  const updateStep = (i, config) => setSteps(steps.map((s, idx) => idx === i ? { ...s, config: { ...s.config, ...config } } : s));

  const save = async () => {
    setSaving(true);
    try {
      const body = { name, trigger_type: trigger, trigger_config: {}, steps };
      if (initial?.id) await api.put(`/tenant/marketing/automations/${initial.id}`, body);
      else await api.post('/tenant/marketing/automations', body);
      toast.success('Automatización guardada'); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial?.id ? 'Editar' : 'Crear'} automatización</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><label className="text-xs font-semibold text-[#64748B]">Nombre</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" /></div>
          <div><label className="text-xs font-semibold text-[#64748B]">¿Cuándo se activa?</label>
            <select value={trigger} onChange={e => setTrigger(e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg">
              {Object.entries(TRIGGER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          <div><label className="text-xs font-semibold text-[#64748B]">Pasos ({steps.length}/5)</label>
            <div className="space-y-2 mt-1">
              {steps.map((s, i) => (
                <div key={i} className="p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-[#0F172A]">{STEP_TYPES.find(t => t.value === s.type)?.label || s.type}</span>
                    <button onClick={() => removeStep(i)} className="text-[#94A3B8] hover:text-red-500 text-xs">Quitar</button>
                  </div>
                  {s.type === 'wait' && (() => {
                    const { unit, value } = getWaitDisplay(s.config);
                    return (
                      <div className="flex items-center gap-2">
                        <input type="number" min={1} value={value} onChange={e => updateStep(i, buildWaitConfig(unit, e.target.value))}
                          className="w-20 text-xs border border-[#E2E8F0] rounded px-2 py-1" data-testid="automation-wait-value" />
                        <select value={unit} onChange={e => updateStep(i, buildWaitConfig(e.target.value, value))}
                          className="text-xs border border-[#E2E8F0] rounded px-2 py-1" data-testid="automation-wait-unit">
                          {WAIT_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                        </select>
                      </div>
                    );
                  })()}
                  {s.type === 'send_template' && <select value={s.config?.template_id || ''} onChange={e => updateStep(i, { template_id: e.target.value })} className="w-full text-xs border border-[#E2E8F0] rounded px-2 py-1" data-testid="campaign-template-select">
                    <option value="">Seleccionar plantilla</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.status_label})</option>)}</select>}
                  {s.type === 'send_whatsapp_direct' && <textarea value={s.config?.text || ''} onChange={e => updateStep(i, { text: e.target.value })}
                    placeholder="Ej: Hola, ¿sigues interesado? Aquí estamos para ayudarte." rows={2}
                    className="w-full text-xs border border-[#E2E8F0] rounded px-2 py-1.5 resize-none" data-testid="whatsapp-direct-text" />}
                  {s.type === 'escalate_to_human' && <input value={s.config?.message || ''} onChange={e => updateStep(i, { message: e.target.value })}
                    placeholder="Mensaje para el vendedor (opcional, usamos uno por default)"
                    className="w-full text-xs border border-[#E2E8F0] rounded px-2 py-1" data-testid="escalate-message-input" />}
                  {s.type === 'stop_if_replied' && <p className="text-[11px] text-[#94A3B8]">Si el lead ya respondió, la secuencia se detiene aquí y no sigue insistiendo.</p>}
                  {s.type === 'outbound_voice_call' && <p className="text-[11px] text-[#94A3B8]">Tu asistente de IA le marca por teléfono y conversa con la persona — no necesitas configurar nada.</p>}
                  {s.type === 'connect_lead_to_vendor' && (
                    <div className="space-y-2">
                      <div>
                        <label className="text-[10px] text-[#94A3B8]">Modo de enlace</label>
                        <select value={s.config?.mode || 'ivr'} onChange={e => updateStep(i, { mode: e.target.value })}
                          className="w-full mt-0.5 text-xs border border-[#E2E8F0] rounded px-2 py-1" data-testid="vendor-link-mode">
                          <option value="ivr">IVR — el lead presiona 1</option>
                          <option value="bridge">Bridge — marcar a ambos a la vez</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-[#94A3B8]">Vendedor</label>
                        <select value={s.config?.vendor_resolution || 'assigned'} onChange={e => updateStep(i, { vendor_resolution: e.target.value })}
                          className="w-full mt-0.5 text-xs border border-[#E2E8F0] rounded px-2 py-1" data-testid="vendor-link-resolution">
                          <option value="assigned">Asignado al lead</option>
                          <option value="on_duty_guard">De guardia</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-[10px] text-[#94A3B8] shrink-0">Espera mín. entre intentos</label>
                        <input type="number" min={1} max={120} value={s.config?.cooldown_minutes ?? 5}
                          onChange={e => updateStep(i, { cooldown_minutes: parseInt(e.target.value, 10) || 5 })}
                          className="w-16 text-xs border border-[#E2E8F0] rounded px-2 py-1" data-testid="vendor-link-cooldown" />
                        <span className="text-[10px] text-[#94A3B8]">min</span>
                      </div>
                      <label className="flex items-center gap-2 text-[11px] text-[#64748B]">
                        <input type="checkbox" checked={s.config?.fallback_notify_vendor !== false}
                          onChange={e => updateStep(i, { fallback_notify_vendor: e.target.checked })} data-testid="vendor-link-fallback" />
                        Si falla la voz, avisar al vendedor por WhatsApp
                      </label>
                      <p className="text-[11px] text-[#94A3B8]">{STEP_TYPES.find(t => t.value === s.type)?.help}</p>
                    </div>
                  )}
                  {(s.type === 'ask_nps' || s.type === 'ask_csat' || s.type === 'ask_ces') && (
                    <p className="text-[11px] text-[#94A3B8]">{STEP_TYPES.find(t => t.value === s.type)?.help || 'Encuesta pendiente de respuesta del cliente.'}</p>
                  )}
                  {s.type === 'send_sms' && (
                    <textarea value={s.config?.text || ''} onChange={e => updateStep(i, { text: e.target.value })}
                      placeholder="Ej: Hola, te recordamos tu cita de mañana."
                      rows={2}
                      className="w-full text-xs border border-[#E2E8F0] rounded px-2 py-1.5 resize-none" data-testid="sms-direct-text" />
                  )}
                  {s.type === 'send_sms' && <p className="text-[11px] text-[#94A3B8] mt-1">Usa Twilio (misma cuenta que voz). Respeta consentimiento SMS y horario laboral.</p>}
                  {s.type === 'send_email' && (
                    <>
                      <select value={s.config?.email_template_id || ''} onChange={e => updateStep(i, { email_template_id: e.target.value })}
                        className="w-full text-xs border border-[#E2E8F0] rounded px-2 py-1" data-testid="automation-email-template-select">
                        <option value="">Seleccionar plantilla de email</option>
                        {emailTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                      <p className="text-[11px] text-[#94A3B8] mt-1">{STEP_TYPES.find(t => t.value === s.type)?.help}</p>
                    </>
                  )}
                  {s.type === 'mcp_tool_call' && (
                    <>
                      <select value={s.config?.mcp_server_tool_id || ''} onChange={e => updateStep(i, { mcp_server_tool_id: e.target.value })}
                        className="w-full text-xs border border-[#E2E8F0] rounded px-2 py-1" data-testid="automation-mcp-tool-select">
                        <option value="">Seleccionar herramienta MCP</option>
                        {mcpTools.map(t => <option key={t.id} value={t.id}>{t.server_name}: {t.display_name || t.external_tool_name}</option>)}
                      </select>
                      <p className="text-[11px] text-[#94A3B8] mt-1">{STEP_TYPES.find(t => t.value === s.type)?.help}</p>
                    </>
                  )}
                </div>
              ))}
            </div>
            {steps.length < 5 && (
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {STEP_TYPES.map(st => <Button key={st.value} variant="outline" size="sm" onClick={() => addStep(st.value)} className="text-xs">
                  <st.icon className="w-3 h-3 mr-1" /> {st.label}</Button>)}
              </div>
            )}
          </div>
          <Button onClick={save} disabled={saving || !name || steps.length === 0} className="w-full bg-[#0F172A] text-white">{saving ? 'Guardando...' : 'Guardar'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Confirmación antes de lanzar una prueba. El texto explica en palabras simples
// las 3 consecuencias reales: solo corre si está activa, corre todas las que
// comparten el mismo disparador, y no le manda mensajes a ningún cliente.
function RunTestDialog({ flow, onClose, onDone }) {
  const [running, setRunning] = useState(false);

  const launch = async () => {
    setRunning(true);
    try {
      const res = await api.post(`/tenant/marketing/automations/${flow.id}/run-test`);
      toast.success(res.data?.message || 'Prueba iniciada. Revisa el Historial en unos segundos.');
      onDone(flow.id);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No pudimos iniciar la prueba. Inténtalo de nuevo.');
      setRunning(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => { if (!running) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Probar «{flow.name}»</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#64748B]">
            Vamos a correr esta automatización ahora mismo, sin esperar a que ocurra el evento
            «{TRIGGER_LABELS[flow.trigger_type] || flow.trigger_type}». Antes de continuar, ten en cuenta:
          </p>
          <ul className="text-xs text-[#64748B] space-y-1.5 list-disc pl-4">
            <li><strong>Ningún cliente recibe mensajes.</strong> Como la prueba no usa un cliente real, los pasos que mandan WhatsApp, SMS, correo o llamadas se anotan como omitidos.</li>
            <li><strong>Se prueban todas las automatizaciones activas con el mismo disparador</strong>, no solo esta. Si tienes otras que arrancan con «{TRIGGER_LABELS[flow.trigger_type] || flow.trigger_type}», también correrán.</li>
            <li><strong>Solo corre si está activa.</strong> Las que estén en borrador o pausadas no hacen nada.</li>
            <li>El resultado queda anotado en <strong>Historial</strong>, para que revises paso por paso qué pasó.</li>
          </ul>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={running} data-testid="automation-run-test-cancel">Cancelar</Button>
            <Button className="flex-1 bg-[#0F172A] text-white" onClick={launch} disabled={running} data-testid="automation-run-test-confirm">
              {running ? 'Iniciando...' : 'Sí, probar ahora'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RunsModal({ flowId, onClose }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingRunId, setSavingRunId] = useState(null);

  // Carga perezosa: solo se pide al abrir este panel.
  const loadRuns = useCallback(() => {
    setLoading(true);
    setError('');
    api.get(`/tenant/marketing/automations/${flowId}/runs`)
      .then(r => setRuns(r.data || []))
      .catch(e => setError(formatApiError(e.response?.data?.detail) || 'No pudimos cargar el historial.'))
      .finally(() => setLoading(false));
  }, [flowId]);
  useEffect(() => { loadRuns(); }, [loadRuns]);

  const saveScore = async (run, score) => {
    const surveyType = String(run.context?.survey_type || '').toLowerCase();
    if (!SURVEY_TYPES.includes(surveyType)) { toast.error('Esta ejecución no tiene una encuesta válida para calificar.'); return; }
    setSavingRunId(run.id);
    try {
      await api.post(`/tenant/marketing/automations/runs/${run.id}/survey-response`, { survey_type: surveyType, score });
      toast.success('Calificación guardada');
      loadRuns();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No pudimos guardar la calificación.');
    }
    setSavingRunId(null);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Historial de ejecuciones</DialogTitle></DialogHeader>
        <p className="text-xs text-[#64748B] -mt-2">Cada línea es una vez que se ejecutó esta automatización. Si dejó una encuesta pendiente, aquí puedes capturar la calificación que te dio el cliente.</p>
        {loading ? (
          <p className="text-sm text-[#64748B] text-center py-4">Cargando historial...</p>
        ) : error ? (
          <div className="text-center py-4 space-y-2">
            <p className="text-sm text-red-600">{error}</p>
            <Button variant="outline" size="sm" onClick={loadRuns} data-testid="automation-runs-retry">Reintentar</Button>
          </div>
        ) : runs.length === 0 ? (
          <p className="text-sm text-[#64748B] text-center py-4">Sin ejecuciones todavía.</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {runs.map(r => {
              const surveyType = String(r.context?.survey_type || '').toLowerCase();
              const showSurvey = r.context?.survey_pending === true && SURVEY_TYPES.includes(surveyType);
              return (
                <div key={r.id} className="p-2 bg-[#F8FAFC] rounded-lg text-xs" data-testid="automation-run-row">
                  <p className="text-[#0F172A]">Paso {r.current_step + 1} · {RUN_STATUS_LABELS[r.status] || r.status}
                    {r.nps_score != null && ` · NPS: ${r.nps_score}`}
                    {r.csat_score != null && ` · CSAT: ${r.csat_score}`}
                    {r.ces_score != null && ` · CES: ${r.ces_score}`}</p>
                  <p className="text-[#94A3B8]">{r.created_at ? new Date(r.created_at).toLocaleString('es-MX') : 'Sin fecha'}</p>
                  {showSurvey && (
                    <div className="mt-2 pt-2 border-t border-[#E2E8F0]" data-testid="automation-survey-pending">
                      <div className="flex items-center gap-1.5">
                        <Star className="w-3 h-3 text-amber-500 shrink-0" />
                        <span className="text-[11px] font-medium text-[#0F172A]">Encuesta pendiente — {SURVEY_LABELS[surveyType]}</span>
                        <HelpTooltip text="Captura aquí la calificación que te dio el cliente por WhatsApp, por teléfono o en persona. 1 es muy mal y 5 es excelente. Se guarda al momento y a partir de ahí cuenta en tu tablero de satisfacción y en los reportes." />
                      </div>
                      <div className="flex items-center gap-1 mt-1.5">
                        {[1, 2, 3, 4, 5].map(n => (
                          <Button key={n} variant="outline" size="sm" className="h-7 w-8 p-0 text-xs"
                            disabled={savingRunId === r.id}
                            title={SURVEY_SCORE_HINTS[n] || `Calificación ${n}`}
                            onClick={() => saveScore(r, n)}
                            data-testid={`automation-survey-score-${n}`}>{n}</Button>
                        ))}
                        <span className="text-[10px] text-[#94A3B8] ml-1">
                          {savingRunId === r.id ? 'Guardando...' : '1 = muy mal · 5 = excelente'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
